import { Controller, useForm } from "react-hook-form";
import { StyledDialog } from "../../../components/StyledComponents/Dialog/Dialog";
import { useTheme } from "../../../ThemeContext/ThemeContext.js";
import {
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  TextField,
} from "@mui/material";
import {
  BodyLarge,
  BodyMedium,
  H6HeadlineSmall,
  H8TitleMedium,
} from "../../../components/StyledComponents/Typography/Typography.tsx";
import {
  PrimaryButton,
  SecondaryButton,
  StyledIconButton,
  StyledToggleButton,
} from "../../../components/StyledComponents/Buttons/AuradineButtons.js";
import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { maxBy } from "lodash";
import {
  StyledFormControl,
  StyledTextField,
  StyledOutlinedInput,
  StyledSelect,
} from "../../../components/StyledComponents/Inputs/Inputs.js";
import { StyledMenuItem } from "../../../components/StyledComponents/Menu/Menu.tsx";
import useAppContextProvider from "../../../AppContext/useAppContextProvider.js";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PostUpdateSiteLocation } from "../../../api/api";
import { usePostNewSite } from "../../queries/usePostNewSite.ts";
import { useGetSiteLists } from "../../../api/queries/useGetSiteLists.ts";
import { useAuth0 } from "@auth0/auth0-react";
import { usePostNewDG } from "../../queries/usePostNewDG.ts";

const SiteAndGroupNamingFormHeader = ({
  setOpen,
  formReset,
  siteConfig,
  siteAndGroupInfo,
}) => {
  const { colors } = useTheme();
  const selectedSiteName = Array.isArray(siteAndGroupInfo)
    ? siteAndGroupInfo.find((site) => site.selectedSite)?.siteName
    : null;
  console.log("siteAndGroupInfo!!", siteAndGroupInfo);
  console.log("selectedSiteName", selectedSiteName);
  return (
    <Box
      display={"flex"}
      flexDirection={"row"}
      justifyContent={"space-between"}
      flexGrow={1}
      padding={"1.5rem 0"}
    >
      <Box display={"flex"} flexDirection={"column"} gap={2}>
        {selectedSiteName ? (
          <H6HeadlineSmall>
            Update the location for:{" "}
            <span style={{ color: colors?.text?.muted, fontWeight: "normal" }}>
              {selectedSiteName || ""}
            </span>
          </H6HeadlineSmall>
        ) : (
          <H6HeadlineSmall>Add a new Site Location</H6HeadlineSmall>
        )}
        <BodyMedium>Add or update the address for this site.</BodyMedium>
      </Box>
      <StyledIconButton 
        colors={colors}
        onClick={() => {
          setOpen(false);
          formReset();
        }}
      >
        <CloseIcon />
      </StyledIconButton>
    </Box>
  );
};

const AddAddressForm = ({
  onSubmit: onOverallSubmit,
  setOpen,
  dataConfig,
  sitesAndGroupInfo,
  sitesAndGroupInfo: siteAndGroupInfo,
  refetch,
}) => {
  const { colors } = useTheme();
  const { setToastMessage, setToastOpen, authToken } = useAppContextProvider();
  const selectedSiteName = Array.isArray(siteAndGroupInfo)
    ? siteAndGroupInfo.find((site) => site.selectedSite)?.siteName
    : "";
  const selectedSiteLocation = Array.isArray(siteAndGroupInfo)
    ? siteAndGroupInfo.find((site) => site.selectedSite)?.siteLocation
    : "";

  const onSubmit = (data) => {
    console.log("data", data);
    const payload = {
      siteName: selectedSiteName,
      siteLocation: data.siteLocation.trim(),
    };
    mutation.mutate({ token: authToken, body: payload });
    setOpen(false);
  };

  const mutation = useMutation({
    mutationFn: ({ token, body }) => PostUpdateSiteLocation(token, body),
    onSuccess: async (response) => {
      setToastOpen(true);
      setToastMessage(response.msg ? response.msg : "Site location updated");
      refetch();
    },
    onError: async (error) => {
      console.error("Mutation error:", error);
      setToastOpen(true);
      setToastMessage(`Failed to update site location!: ${error.message}`);
    },
  });

  console.log("sitesAndGroupInfo", sitesAndGroupInfo);
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      siteLocation: selectedSiteLocation || "",
    },
  });

  const siteLocation = watch("siteLocation");

  return (
    <>
      <SiteAndGroupNamingFormHeader
        setOpen={setOpen}
        formReset={reset}
        siteConfig={dataConfig}
        siteAndGroupInfo={siteAndGroupInfo}
      />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box display={"flex"} flexDirection={"column"} gap={6}>
          <Box display={"flex"} flexDirection={"column"} gap={4}>
            <H8TitleMedium>
              {selectedSiteLocation
                ? "Update Site Location"
                : "Add Site Location"}
            </H8TitleMedium>

            {/* Hidden field for site name */}
            <Controller
              name="siteName"
              control={control}
              render={({ field }) => <input type="hidden" {...field} />}
            />

            <Box display="flex" flexDirection="row" flexGrow={1}>
              <StyledFormControl colors={colors} fullWidth margin="none">
                <Controller
                  name="siteLocation"
                  control={control}
                  defaultValue={selectedSiteLocation || ""}
                  rules={{
                    required: "Site location is required",
                    minLength: {
                      value: 1,
                      message: "Site location cannot be empty",
                    },
                    maxLength: {
                      value: 512,
                      message: "Site location must be 512 characters or fewer",
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <StyledTextField colors={colors}
                      {...field}
                      label="Site Location"
                      id="site-location-input"
                      placeholder="Enter a site location"
                      style={{
                        flex: 1,
                        margin: 0,
                        minHeight: "2.5rem",
                      }}
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error ? fieldState.error.message : ""
                      }
                      onBlur={(e) => {
                        const trimmedValue = e.target.value.trim();
                        field.onChange(trimmedValue);
                        field.onBlur();
                      }}
                    />
                  )}
                />
              </StyledFormControl>
            </Box>
          </Box>

          <Box display={"flex"} justifyContent={"flex-end"} flexGrow={1}>
            <DialogActions>
              <PrimaryButton colors={colors}
                disabled={
                  !siteLocation ||
                  siteLocation.trim() === "" ||
                  !selectedSiteName ||
                  selectedSiteName.trim() === ""
                }
                type="submit"
              >
                Submit
              </PrimaryButton>
            </DialogActions>
          </Box>
        </Box>
      </form>
    </>
  );
};

const AddAddressDialog = ({
  open,
  setOpen,
  sitesAndGroupInfo,
  setSiteAndGroups,
  refetch,
  role,
  siteLists,
}) => {
  const { colors } = useTheme();
  return (
    <StyledDialog colors={colors} maxWidth={"md"} fullWidth open={open} hideBackdrop>
      <DialogContent sx={{ padding: 0 }}>
        <AddAddressForm
          setOpen={setOpen}
          sitesAndGroupInfo={sitesAndGroupInfo}
          refetch={refetch}
        />
      </DialogContent>
    </StyledDialog>
  );
};

export default AddAddressDialog;
